import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Zap, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const HERO_IMG =
  "https://images.unsplash.com/photo-1709626011485-6fe000ea2dbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMG1vZGVybiUyMGdlb21ldHJpYyUyMGZpbmFuY2UlMjBkYXRhfGVufDB8fHx8MTc4NDA1ODU0MXww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@optikurve.com");
  const [password, setPassword] = useState("admin123");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back, admin");
      navigate("/dashboard");
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* LEFT: Form */}
      <div className="flex flex-col justify-between p-8 lg:p-12">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center">
            <Zap className="w-4 h-4 text-primary-foreground" strokeWidth={2.2} />
          </div>
          <div className="leading-tight">
            <div className="font-display text-lg font-semibold tracking-tight">OptiKurve</div>
            <div className="label-caps text-[0.6rem]">Event Gateway · v3.4</div>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center py-12">
          <div className="w-full max-w-md">
            <div className="label-caps mb-3">Secure sign-in · On-premise</div>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mb-3">
              Detect. Value. Decide.
            </h1>
            <p className="text-muted-foreground text-base mb-10 max-w-sm leading-relaxed">
              Real-time event orchestration for financial platforms.
              Sign in to your OptiKurve workspace.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="label-caps">Email</Label>
                <Input
                  id="email"
                  data-testid="login-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="label-caps">Password</Label>
                <Input
                  id="password"
                  data-testid="login-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11"
                />
              </div>

              <Button
                type="submit"
                data-testid="login-submit"
                disabled={loading}
                className="w-full h-11 bg-primary hover:bg-[#0B2B4E] text-primary-foreground font-medium tracking-wide group"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Authenticating…</>
                ) : (
                  <>Sign in <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" /></>
                )}
              </Button>
            </form>

            <div className="mt-8 pt-6 border-t border-border">
              <div className="text-xs text-muted-foreground">
                <span className="label-caps">Demo credentials</span>
                <div className="mt-2 font-mono text-xs">
                  admin@optikurve.com / admin123
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>© 2026 OptiKurve Labs</span>
          <span className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 pulse-dot" />
            All systems operational
          </span>
        </div>
      </div>

      {/* RIGHT: Hero visual */}
      <div className="hidden lg:block relative overflow-hidden border-l border-border">
        <img
          src={HERO_IMG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#081C2D]/85 via-[#081C2D]/50 to-[#124273]/40" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2 text-xs">
            <span className="label-caps text-white/80">Live workspace</span>
          </div>

          <div className="space-y-6 max-w-lg">
            <div className="inline-flex items-center gap-2 border border-white/20 rounded-full px-3 py-1 text-[0.7rem] tracking-wide backdrop-blur-md">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E9B44C] pulse-dot" />
              PROCESSING · 3,842 evt/min
            </div>
            <div className="font-display text-4xl leading-tight font-semibold tracking-tight">
              Turn every signal into an action.
            </div>
            <p className="text-white/70 text-sm leading-relaxed max-w-md">
              OptiKurve ingests events from web, mobile, POS, ATM and core
              banking. Route them through visual workflows or let AI surface
              what you didn&apos;t know to look for.
            </p>

            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-white/10">
              <Stat value="142M" label="Events / month" />
              <Stat value="< 40ms" label="Median latency" />
              <Stat value="99.99%" label="Uptime SLA" />
            </div>
          </div>

          <div className="text-[0.65rem] text-white/50 tracking-widest uppercase font-mono">
            optikurve · secure enclave · region eu-west-1
          </div>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ value, label }) => (
  <div>
    <div className="font-display text-2xl font-semibold tracking-tight">{value}</div>
    <div className="text-[0.7rem] text-white/60 uppercase tracking-widest mt-1">{label}</div>
  </div>
);
